'use client';

import React from 'react';

interface SkeletonProps {
  className?: string;
}

export function Skeleton({ className = '' }: SkeletonProps) {
  return (
    <div
      className={`animate-pulse rounded-lg ${className}`}
      style={{ background: 'var(--bg-secondary)' }}
    />
  );
}

export function TemplateCardSkeleton() {
  return (
    <div
      className="rounded-2xl overflow-hidden transition-all"
      style={{ background: 'var(--bg-card)', border: '1px solid var(--border)' }}
    >
      {/* Image skeleton */}
      <div className="relative aspect-[4/3]">
        <Skeleton className="w-full h-full" />
      </div>

      {/* Content skeleton */}
      <div className="p-4">
        {/* Platform tag */}
        <Skeleton className="h-5 w-20 mb-3" />

        {/* Title */}
        <Skeleton className="h-6 w-3/4 mb-2" />

        {/* Rating */}
        <div className="flex items-center gap-1 mb-3">
          <Skeleton className="h-4 w-24" />
        </div>

        {/* Price and button */}
        <div className="flex items-center justify-between">
          <Skeleton className="h-7 w-16" />
          <Skeleton className="h-9 w-24 rounded-lg" />
        </div>
      </div>
    </div>
  );
}

export function TemplateDetailSkeleton() {
  return (
    <div className="max-w-8xl mx-auto px-6 py-10">
      <div className="grid lg:grid-cols-[1fr_380px] gap-10">
        {/* Left: Gallery + Tabs */}
        <div>
          {/* Main image skeleton */}
          <div
            className="rounded-2xl overflow-hidden mb-3"
            style={{ border: '1px solid var(--border)' }}
          >
            <div className="relative aspect-video">
              <Skeleton className="w-full h-full" />
            </div>
          </div>

          {/* Tabs skeleton */}
          <div
            className="flex gap-1 p-1 rounded-xl mb-6"
            style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border)' }}
          >
            <Skeleton className="h-10 flex-1 rounded-lg" />
            <Skeleton className="h-10 flex-1 rounded-lg" />
            <Skeleton className="h-10 flex-1 rounded-lg" />
          </div>

          {/* Content skeleton */}
          <div className="space-y-3">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-3/4" />
          </div>
        </div>

        {/* Right: Sidebar */}
        <div>
          <div
            className="rounded-2xl p-6 sticky top-20"
            style={{ background: 'var(--bg-card)', border: '1px solid var(--border)' }}
          >
            {/* Title */}
            <Skeleton className="h-8 w-3/4 mb-2" />
            <Skeleton className="h-5 w-1/2 mb-4" />

            {/* Rating */}
            <Skeleton className="h-6 w-32 mb-6" />

            {/* Price */}
            <Skeleton className="h-10 w-24 mb-6" />

            {/* Buttons */}
            <Skeleton className="h-12 w-full mb-3 rounded-xl" />
            <Skeleton className="h-12 w-full rounded-xl" />

            {/* Details */}
            <div className="mt-6 space-y-4">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-full" />
            </div>
          </div>
        </div>
      </div>

      {/* Related templates skeleton */}
      <div className="mt-16">
        <Skeleton className="h-8 w-48 mb-6" />
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          <TemplateCardSkeleton />
          <TemplateCardSkeleton />
          <TemplateCardSkeleton />
        </div>
      </div>
    </div>
  );
}

export function CategoryFilterSkeleton() {
  return (
    <div className="flex flex-col gap-1.5">
      {[1, 2, 3, 4, 5].map((i) => (
        <Skeleton key={i} className="h-9 w-full rounded-lg" />
      ))}
    </div>
  );
}

export function SpinnerLoader({ size = 32, className = '' }: { size?: number; className?: string }) {
  return (
    <svg
      className={`animate-spin ${className}`}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      style={{ color: 'var(--accent)' }}
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
      />
    </svg>
  );
}

export function PurchaseCardSkeleton() {
  return (
    <div
      className="flex gap-4 p-4 rounded-2xl"
      style={{ background: 'var(--bg-card)', border: '1px solid var(--border)' }}
    >
      {/* Thumbnail skeleton */}
      <div className="w-32 h-20 rounded-xl overflow-hidden flex-shrink-0">
        <Skeleton className="w-full h-full" />
      </div>

      {/* Content skeleton */}
      <div className="flex-1">
        {/* Title */}
        <Skeleton className="h-5 w-48 mb-2" />
        
        {/* Order info */}
        <Skeleton className="h-4 w-64 mb-3" />
        
        {/* Price and status */}
        <div className="flex items-center gap-2">
          <Skeleton className="h-5 w-12" />
          <Skeleton className="h-5 w-16 rounded" />
        </div>
      </div>

      {/* Button skeleton */}
      <div className="flex items-center">
        <Skeleton className="h-10 w-28 rounded-lg" />
      </div>
    </div>
  );
}

export function LicenseKeySkeleton() {
  return (
    <div
      className="p-5 rounded-2xl"
      style={{ background: 'var(--bg-card)', border: '1px solid var(--border)' }}
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          {/* Template name */}
          <Skeleton className="h-5 w-48 mb-2" />
          {/* Order number */}
          <Skeleton className="h-4 w-32" />
        </div>
        {/* Status badge */}
        <Skeleton className="h-6 w-16 rounded-lg" />
      </div>

      {/* License key input */}
      <div className="flex items-center gap-2">
        <Skeleton className="flex-1 h-12 rounded-xl" />
        <Skeleton className="h-12 w-24 rounded-lg" />
      </div>

      {/* Activation date */}
      <Skeleton className="h-4 w-40 mt-3" />
    </div>
  );
}

export default Skeleton;